import {
  GraphType,
  Node,
  Relationship,
} from './api/APITypes';
import Graph from 'graphology';
import { GraphView, Importance, FilterDefaultOptions } from './state/ViewTypes';
import { theme } from '@chakra-ui/theme';
import { v4 as uuidv4 } from 'uuid';
import { Attributes } from 'react';
import { systemLightMode } from '../theme';

function graphFromData(data: GraphType, centerNodeId: string, mergeGraph?: Graph): Graph {
  let centerNodeElementId: string | undefined = undefined;

  const graph = mergeGraph ? mergeGraph : new Graph({ type: 'mixed' });
  const badNodes: string[] = [];

  if (mergeGraph) {
    centerNodeElementId = graph.findNode(
      (node) => graph.getNodeAttributes(node).id === centerNodeId,
    );
  }

  for (let i = 0; i < data.nodes.length; i++) {
    let node: Node = data.nodes[i];

    if (!Object.hasOwn(node, 'label') || !Object.hasOwn(node, 'label')) {
      badNodes.push(node.elementId);
      continue;
    }

    if (node.id === centerNodeId) centerNodeElementId = data.nodes[i].elementId;

    addNodeToGraph(node, graph, node.id === centerNodeId);
  }

  for (let i = 0; i < data.edges.length; i++) {
    let edge: Relationship = data.edges[i];

    if (badNodes.includes(edge.startNodeElementId) || badNodes.includes(edge.endNodeElementId)) {
      continue;
    }

    try {
      addEdgeToGraph(edge, graph, centerNodeElementId!);
    } catch (e) {
      console.error(e);
    }
  }
  setNodeSizeBasedOnDegree(graph, useOutDegree);

  return graph;
}

function addNodeToGraph(node: Node, graph: Graph, isCenter?: boolean) {
  
  if (!Object.hasOwn(node, 'label') || !Object.hasOwn(node, 'label')) return;
  if (graph.hasNode(node.elementId)) return;
  
  let position: { x: number; y: number } = findValidPosition(graph, node);
  
  graph.addNode(node.elementId, {
    label: node.label ? node.label.replace('CASE OF', '') : `oopsie ${node.id}`,
    id: node.id,
    judgementdate: node.specific_properties.judgementdate,
    nodeType: 'DOCUMENT',
    importance: node.specific_properties.importance,
    country: node.specific_properties.country,
    chambertype: node.specific_properties.chambertype,
    separateopinion: node.specific_properties.separateopinion,
    keywords: node.specific_properties.keywords,
    x: position.x,
    y: position.y, //Math.random() * 200000000000 - 200000000000 / 2,
    hidden: false,
    labelSize: '5px',
    color: isCenter
      ? theme.colors.purple[400]
      : getNodeColor(node.specific_properties.importance?.toString()),
    originalColor: isCenter
      ? theme.colors.purple[400]
      : getNodeColor(node.specific_properties.importance?.toString()),
    size: 10, //sets the initial size which is updated later
    type: 'border',
    originalBorderColor: getNodeBorderColor(
      node.specific_properties.violation ?? '',
      node.specific_properties.nonviolation ?? '',
    ),
    borderColor: getNodeBorderColor(
      node.specific_properties.violation ?? '',
      node.specific_properties.nonviolation ?? '',
    ),
    violation: node.specific_properties.violation,
    nonviolation: node.specific_properties.nonviolation,
    url: node.url,
  });
}

function getEdgeColor(edge: Relationship): string {
  // TODO: Refactor GraphFactory to style according to ThemeSystemProvider

  switch (edge.type) {
    case 'recommend':
      return systemLightMode.token('colors.ai');
    case 'Continues':
      return 'blue';
    case 'Cites':
      return 'gray';
    default:
      return 'black';
  }
}

function addEdgeToGraph(edge: Relationship, graph: Graph, centerNode: string) {
  if (graph.hasEdge(edge.elementId)) return;
  if (graph.hasEdge(edge.startNodeElementId, edge.endNodeElementId)) return;
  
  graph.addDirectedEdgeWithKey(edge.elementId, edge.startNodeElementId, edge.endNodeElementId, {
    label: edge.type,
    size: 2,
    hidden: !(edge.startNodeElementId === centerNode) && !(edge.endNodeElementId === centerNode),
    color: getEdgeColor(edge),
  });
}

function getDefaultGraphView(centerNode?: string): GraphView {
  let graphView: GraphView = {
    name: 'Graph',
    id: uuidv4(),
    centerNode: centerNode,
    floatingDocuments: [],
    serializedGraph: null,
    filter: FilterDefaultOptions
    };
    
  return graphView;
}

function findValidPosition(graph: Graph, inputNode: Node): { x: number; y: number } {
  
  let position = {
    x: new Date(inputNode.date!).getTime(),
    y: Math.random() * 200000000000 - 200000000000 / 2,
  };
  
  const maxSize = 10000000000;

  if (graph.nodes().length === 0) return position;
  let overlap = true;

  while (overlap) {
    overlap = false; // Assume no overlap unless we find one

    graph.forEachNode((existingNode) => {
      
      const leftBoundExistingNode = graph.getNodeAttribute(existingNode, 'x') - maxSize;
      const rightBoundExistingNode = graph.getNodeAttribute(existingNode, 'x') + maxSize;

      const leftBoundInputNode = position.x - maxSize;
      const rightBoundInputNode = position.x + maxSize;

      const topBoundExistingNode = graph.getNodeAttribute(existingNode, 'y') + maxSize;
      const bottomBoundExistingNode = graph.getNodeAttribute(existingNode, 'y') - maxSize;

      const topBoundInputNode = position.y + maxSize;
      const bottomBoundInputNode = position.y - maxSize;

      if (
        (leftBoundExistingNode <= rightBoundInputNode &&
          rightBoundInputNode <= rightBoundExistingNode) ||
        (rightBoundExistingNode >= leftBoundInputNode &&
          leftBoundInputNode >= leftBoundExistingNode)
      ) {
        if (
          (topBoundExistingNode >= bottomBoundInputNode &&
            bottomBoundInputNode >= bottomBoundExistingNode) ||
          (bottomBoundExistingNode <= topBoundInputNode &&
            topBoundInputNode <= topBoundExistingNode)
        ) {
          position.y += 1000000000;
          overlap = true;
        }
      }
    });
  }
  return position;
}

function setNodeSizeBasedOnDegree(graph: Graph, useOutDegree: boolean) {
  //also updates when you expand neighbours
  //todo: update when reccomended neighbours (undirected edge?)

  graph.forEachNode((node) => {
    const degree = useOutDegree ? graph.outDegree(node) : graph.inDegree(node);
    let nodeSize = 10;
    if (degree < 10) {
      nodeSize = 10;
    } else if (degree > 30) {
      nodeSize = 30;
    } else {
      nodeSize = nodeSize + degree;
    }
    graph.setNodeAttribute(node, 'size', nodeSize);
  });
}

let useOutDegree = true;

//todo: put in filteractions and use this later to switch between in and out degree to determine size
function toggleDegree(graph: Graph) {
  useOutDegree = !useOutDegree;
  setNodeSizeBasedOnDegree(graph, useOutDegree);
}

function getNodeColor(type?: string) {
  switch (type) {
    case 'Keyword':
      return '#fff873';
    case 'Article':
      return '#065666';
    case 'Paragraph':
      return '#ff6e6e';
    case 'SubParagraph':
      return '#ffbfbf';
    case 'Document':
      return '#6B46C1';
    case '0':
      return Importance[0].color;
    case '1':
      return Importance[1].color;
    case '2':
      return Importance[2].color;
    case '3':
      return Importance[3].color;
    case '4':
      return Importance[4].color;
    case undefined:
      return '#A13123';
    default:
      return '#697aff';
  }
}

function getNodeBorderColor(violation: string, nonviolation: string) {
  if (violation && nonviolation) {
    return theme.colors.orange[400];
  } else if (violation) {
    return theme.colors.red[600];
  } else if (nonviolation) {
    return theme.colors.green[600];
  }
  return theme.colors.blue[500];
}

function copyToMutableGraph(immutableGraph: Graph<Node, Attributes, Attributes>) {
  let mutableGraph: Graph<Node, Attributes, Attributes> = new Graph({ type: 'mixed' });

  immutableGraph.forEachNode((node) =>
    mutableGraph.addNode(node, { ...immutableGraph.getNodeAttributes(node) }),
  );
  immutableGraph.forEachEdge((edge) =>
    mutableGraph.addEdge(immutableGraph.source(edge), immutableGraph.target(edge), {
      ...immutableGraph.getEdgeAttributes(edge),
    }),
  );
  return mutableGraph;
}

export {
  graphFromData,
  getNodeColor,
  getNodeBorderColor,
  addNodeToGraph,
  addEdgeToGraph,
  getDefaultGraphView,
  setNodeSizeBasedOnDegree,
  copyToMutableGraph,
};
